import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { get } from 'lodash'
import { Text, Icon, Button } from '@adc/ui-core'
import classnames from 'classnames/bind'
import { gtmNewUserOnboardingClick } from '../../../../shared/services/tracking'
import UserService from '../../../../shared/services/user'
import { InvestorPreferences } from '../../../../util'
import styles from './styles.scss'

const cx = classnames.bind(styles)

const INVESTOR_PREFERENCES_ENDPOINT = '/apps/api/investor/preferences'

const ABTestThankYou = ({ onClick, elmId, featureFlags = {} }) => {
  return (
    <div className={cx('onboarding-page')}>
      <div>
        <Text size='huge' color='success' weight='normal'>
          You're all Set
        </Text>
        <Text
          color='black'
          className={cx('u-mt-2')}
          size='large'
          weight='heavy'
        >
          You're Now Ready to Start Your
          <br /> Wealth Growth Journey!
        </Text>
      </div>
      <div>
        <div className={cx('save-icon-container', 'u-mt-2')}>
          <Icon
            name='house-chimney-blank'
            iconStyle='solid'
            color='adcLightBlue'
            className={cx('save-house', 'fa-flip-horizontal')}
          />
          <Icon
            name='heart'
            iconStyle='regular'
            color='error'
            className={cx('save-heart')}
          />
          <Icon
            name='hand-back-point-up'
            iconStyle='regular'
            color='black'
            className={cx('save-hand-pointer', 'fa-sharp', 'fa-thin')}
          />
        </div>
        <Text color='black' className={cx('u-mt-1')} size='big' weight='heavy'>
          SAVE
        </Text>
      </div>
      <Text
        color='darkCobalt'
        className={cx('u-mt-4')}
        size='tiny'
        weight='black'
      >
        THE POWER OF SAVE
      </Text>
      <Text color='black' className={cx('u-mt-4')} size='small' weight='heavy'>
        Our favorite pro tip?
      </Text>
      <Text color='black' size='small'>
        SAVE the properties you like! It’ll keep <br /> you informed and enhance
        your future
        <br /> recommendations.{' '}
        <a href='/lp/get-started/' data-elm-id={`${elmId}_get_started_link`}>
          Get More Tips
        </a>
      </Text>
      <div className={cx('u-mt-4')}>
        <Button
          role='primary'
          onClick={onClick}
          elmId={`${elmId}_thank_you_cta`}
        >
          Show me Properties
        </Button>
      </div>
    </div>
  )
}

ABTestThankYou.propTypes = {
  onClick: PropTypes.func.isRequired,
  elmId: PropTypes.string,
  featureFlags: PropTypes.object,
}

export const OnboardingHeader = ({
  step,
  backStep,
  currentPage,
  pages,
  elmId,
}) =>
  step >= 1 && (
    <div className={cx('header')}>
      {!currentPage.hideBack && (
        <span
          className={cx('back-button')}
          data-elm-id={`${elmId}_back_button`}
        >
          <Icon
            name='angle-left'
            iconStyle='light'
            color='gray'
            size='large'
            onClick={backStep}
          />
        </span>
      )}
      <span className={cx('steps')} data-elm-id={`${elmId}_steps`}>
        <Text color='gray'>
          {step + 1} of {pages.length}
        </Text>
      </span>
    </div>
  )

OnboardingHeader.propTypes = {
  step: PropTypes.number,
  backStep: PropTypes.func,
  currentPage: PropTypes.object,
  pages: PropTypes.array,
  elmId: PropTypes.string,
}

export const ThankYouPage = ({
  redirectToSearch,
  onClick,
  elmId,
  currentPage,
  user,
  featureFlags = {},
}) => {
  const { remaining_registration_count: remainingRegistrationCount } =
    user.bidder_signature?.[0].current || {}
  const shouldShowRegistrationLimitMessage =
    featureFlags['fe.resi.enable-registration-limit-messaging'] &&
    Number.isInteger(remainingRegistrationCount)

  const enableABTest =
    featureFlags['fe.resi.ab.testing.signup.thank-you-page'] &&
    featureFlags['fe.resi.ab.testing.signup.thank-you-page']?.test.version ===
      'test'

  return enableABTest ? (
    <ABTestThankYou
      onClick={onClick}
      elmId={elmId}
      featureFlags={featureFlags}
    />
  ) : (
    <div className={cx('onboarding-page')}>
      <div className={cx('u-mb-4')}>
        <Text size='huge' color='success' weight='normal'>
          {currentPage.title}
        </Text>
      </div>
      {shouldShowRegistrationLimitMessage && (
        <Text color='gray' size='large' className={cx('u-mb-4')}>
          Congratulations! You can register for up to{' '}
          {remainingRegistrationCount} active properties. Please{' '}
          <a
            target='_BLANK'
            data-elm-id='registration_limit_faq_link'
            // TODO: change this when marketing have specific link
            href='/lp/learn-more/frequently-asked-questions/'
            rel='noreferrer'
          >
            click here
          </a>{' '}
          for more information.
        </Text>
      )}
      <Text color='gray' size='large'>
        Look for updates and recommendations just for you
      </Text>
      <Text color='gray' className={cx('u-mt-4')} size='large'>
        {redirectToSearch
          ? 'Ready to discover our exclusive properties?'
          : 'Ready to build your wealth?'}
      </Text>
      <div className={cx('u-mt-4')}>
        <Button
          role='primary'
          onClick={onClick}
          elmId={`${elmId}_thank_you_cta`}
        >
          {' '}
          {redirectToSearch ? 'Search Now' : `Let's Go`}{' '}
        </Button>
      </div>
      <Text className={cx('u-mt-6')} color='gray'>
        <a
          href='/dashboard/preferences'
          data-elm-id={`${elmId}_preferences_link`}
        >
          Edit your preferences
        </a>{' '}
        in your dashboard at anytime.
      </Text>
    </div>
  )
}

ThankYouPage.propTypes = {
  redirectToSearch: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  currentPage: PropTypes.object,
  elmId: PropTypes.string,
  user: PropTypes.object,
  featureFlags: PropTypes.object,
}

export const OnboardingButton = ({
  elmId,
  code,
  option,
  onClick,
  isActive,
}) => {
  const _onClick = () => {
    gtmNewUserOnboardingClick(option['data-elm-id'])
    onClick(code, option.value)
  }

  return (
    <div
      className={cx('onboarding-large-button', isActive && 'active')}
      data-elm-id={`${elmId}_${option['data-elm-id']}`}
      onClick={_onClick}
    >
      <div className={cx('large-button-icon')}>
        {option.icon ? (
          <Icon
            name={option.icon}
            iconStyle='light'
            data-elm-id={`${elmId}_${option['data-elm-id']}_close`}
            color='gray'
            size='big'
          />
        ) : (
          <img src={option.img} />
        )}
      </div>
      <div className={cx('large-button-text', 'u-mr-4')}>
        <div className={cx('u-mb-1')}>
          <Text weight='heavy' color='gray'>
            {option.label}
          </Text>
        </div>
        <Text size='tiny' color='gray'>
          {option.content}
        </Text>
      </div>
    </div>
  )
}

OnboardingButton.propTypes = {
  elmId: PropTypes.string,
  code: PropTypes.string,
  option: PropTypes.object,
  onClick: PropTypes.func.isRequired,
  isActive: PropTypes.bool,
}

const updateSavedSearches = (savedSearches, question) => {
  const answerCode = get(question, 'answer.value[0].response_code')
  return savedSearches.map(savedSearch => {
    savedSearch.parameters = {
      ...InvestorPreferences.assetType(answerCode),
      ...InvestorPreferences.occupancyStatus(answerCode),
      ...savedSearch.parameters,
    }
    return savedSearch
  })
}

export const OnboardingQuestion = ({
  elmId,
  nextStep,
  currentPage,
  options = [],
  code,
  savedSearches,
  user = {},
}) => {
  const [selected, setSelected] = useState()
  const { label } = currentPage

  const selectOption = async (code, value) => {
    const questionSets = InvestorPreferences.IP_OBJECT()
    const questionValue = { [code]: value }
    const questionSet = InvestorPreferences.formToStore(
      questionSets[code],
      questionValue
    )
    const parsedQuestionSet = [InvestorPreferences.apiValue(questionSet)]
    setSelected(value)
    nextStep()
    try {
      await UserService.investorPreferences(
        INVESTOR_PREFERENCES_ENDPOINT,
        parsedQuestionSet
      )
      await UserService.createSavedSearches(
        user.user_id,
        updateSavedSearches(savedSearches, questionSet)
      )
    } catch (e) {
      // What do we do when we error? Do we really want to show the user an error right after creating an account?
      console.error(e)
    }
  }

  return (
    <div className={cx('onboarding-page')}>
      <div className={cx('u-mb-4')}>
        <Text size='huge' color='success' weight='normal'>
          {(currentPage.title || '').replace('%s', user.first_name)}
        </Text>
      </div>
      <h2 data-elm-id={`${elmId}_question`} className={cx('u-mb-4')}>
        {label}
      </h2>
      {options.map((option, i) => (
        <OnboardingButton
          elmId={`${elmId}_option`}
          key={`option_${i}`}
          onClick={selectOption}
          option={option}
          code={code}
          isActive={selected === option.value}
        />
      ))}
    </div>
  )
}

OnboardingQuestion.propTypes = {
  code: PropTypes.string,
  elmId: PropTypes.string,
  nextStep: PropTypes.func,
  currentPage: PropTypes.object,
  options: PropTypes.object,
  savedSearches: PropTypes.array,
  user: PropTypes.object,
}
